import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useApi } from "@/lib/useApi";
import { api } from "@/lib/api";
import { PageTitle, Panel, Loading, Empty, ErrorState } from "@/components/Common";
import { TxnBadge } from "@/components/StatusPill";
import { Timeline } from "@/components/Timeline";
import { inr, istDateTime, secs, downloadCSV, toEpoch } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Download, Search } from "lucide-react";

const STATUSES = ["", "success", "refunded", "failed", "pending"];

export default function Transactions() {
  const { user } = useAuth();
  const base = user.role === "admin" ? "/admin" : "/merchant";
  const [sp, setSp] = useSearchParams();
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState(sp.get("q") || "");

  const status = sp.get("status") || "";
  const from = sp.get("from") || "";
  const to = sp.get("to") || "";

  const setParam = (k, v) => {
    const next = new URLSearchParams(sp);
    if (v) next.set(k, v); else next.delete(k);
    setSp(next);
  };

  const qs = new URLSearchParams();
  if (status) qs.set("status", status);
  if (from) qs.set("frm", toEpoch(from));
  if (to) qs.set("to", toEpoch(to) + 86399);
  if (sp.get("q")) qs.set("q", sp.get("q"));

  const { data, loading, error, reload } = useApi(`${base}/transactions?${qs.toString()}`);

  const openDetail = async (id) => {
    const { data } = await api.get(`${base}/transactions/${id}`);
    setDetail(data);
  };

  const exportCsv = () => {
    if (!data?.length) return;
    downloadCSV(
      `transactions_${Date.now()}.csv`,
      data.map((t) => ({
        txn_id: t.id, machine: t.machine_code, status: t.status,
        amount_inr: (t.amount_paise / 100).toFixed(2),
        created_ist: istDateTime(t.created_at), paid_ist: istDateTime(t.paid_at),
        ack_ist: istDateTime(t.ack_at), ack_delay_sec: t.ack_delay_sec ?? "",
        payment_ref: t.payment_ref,
      }))
    );
  };

  return (
    <div>
      <PageTitle title={user.role === "admin" ? "Transactions" : "My Transactions"}
        subtitle="Lifecycle: created → payment → dispense ACK → refund">
        <button data-testid="export-csv-btn" onClick={exportCsv}
          className="flex items-center gap-2 rounded-sm border border-border px-3 py-1.5 text-sm hover:bg-[#1c1c1c] transition-colors">
          <Download className="h-3.5 w-3.5" /> Export CSV
        </button>
      </PageTitle>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Status</label>
          <select data-testid="filter-status" value={status} onChange={(e) => setParam("status", e.target.value)}
            className="mt-1 block rounded-sm border border-input bg-[#141414] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-white">
            {STATUSES.map((s) => <option key={s} value={s}>{s ? s[0].toUpperCase() + s.slice(1) : "All statuses"}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground">From</label>
          <input data-testid="filter-from" type="date" value={from} onChange={(e) => setParam("from", e.target.value)}
            className="mt-1 block rounded-sm border border-input bg-[#141414] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-white" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground">To</label>
          <input data-testid="filter-to" type="date" value={to} onChange={(e) => setParam("to", e.target.value)}
            className="mt-1 block rounded-sm border border-input bg-[#141414] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-white" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Search</label>
          <div className="mt-1 flex items-center rounded-sm border border-input bg-[#141414] px-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input data-testid="filter-search" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && setParam("q", q)}
              placeholder="Txn ID or machine code" className="w-full bg-transparent px-2 py-2 text-sm outline-none" />
          </div>
        </div>
      </div>

      <Panel title={`Results${data ? ` (${data.length})` : ""}`}>
        {loading && !data ? <Loading /> : error ? <ErrorState error={error} onRetry={reload} /> :
          data.length === 0 ? <Empty label="No transactions match your filters" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="transactions-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="px-4 py-2 font-medium">Txn ID</th>
                  <th className="px-4 py-2 font-medium">Machine</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium text-right">Amount</th>
                  <th className="px-4 py-2 font-medium text-right">ACK Delay</th>
                  <th className="px-4 py-2 font-medium">Created (IST)</th>
                </tr>
              </thead>
              <tbody>
                {data.map((t) => (
                  <tr key={t.id} onClick={() => openDetail(t.id)} data-testid={`txn-row-${t.id}`}
                    className="border-b border-border/60 cursor-pointer hover:bg-[#161616] transition-colors">
                    <td className="px-4 py-2.5 mono text-xs">{t.id}</td>
                    <td className="px-4 py-2.5 mono">{t.machine_code}</td>
                    <td className="px-4 py-2.5"><TxnBadge status={t.status} /></td>
                    <td className="px-4 py-2.5 mono text-right">{inr(t.amount_paise)}</td>
                    <td className="px-4 py-2.5 mono text-right">{secs(t.ack_delay_sec)}</td>
                    <td className="px-4 py-2.5 text-muted-foreground text-xs">{istDateTime(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="bg-[#0f0f0f] border-border w-full sm:max-w-md overflow-y-auto" data-testid="txn-detail">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="font-head mono text-base">{detail.id}</SheetTitle>
                <SheetDescription className="sr-only">Transaction lifecycle and details</SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Field label="Machine" value={detail.machine_code} mono />
                  <Field label="Status"><TxnBadge status={detail.status} /></Field>
                  <Field label="Amount" value={inr(detail.amount_paise)} mono />
                  <Field label="ACK Delay" value={secs(detail.ack_delay_sec)} mono />
                  <Field label="Payment Ref" value={detail.payment_ref} mono />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-3">Lifecycle</div>
                  <Timeline steps={detail.timeline} />
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Field({ label, value, children, mono }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-0.5 ${mono ? "mono" : ""}`}>{children || value}</div>
    </div>
  );
}
